import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Minus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import GiftCardPromoBanner from "../components/GiftCardPromoBanner";

export default function CartPage() {
  const { cart, updateCartItem, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-4">Sign in to view your cart</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }

  if (!cart?.items?.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-4">Start shopping to add items</p>
        <Link to="/products" className="btn-primary">Browse Products</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8" data-testid="cart-title">Shopping Cart</h1>

      <GiftCardPromoBanner />

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="card" data-testid="cart-items">
            {cart.items.map((item) => (
              <div key={item.product_id} className="flex items-center gap-4 p-4 border-b last:border-b-0">
                <img src={item.image_url || "https://via.placeholder.com/80"} alt={item.name} className="w-20 h-20 object-contain bg-gray-50 rounded" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-800">{item.name}</h3>
                  <p className="text-[#00a99d] font-semibold">&pound;{item.price.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                    className="w-8 h-8 border rounded flex items-center justify-center hover:bg-gray-100"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <button 
                    onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                    className="w-8 h-8 border rounded flex items-center justify-center hover:bg-gray-100"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-semibold w-20 text-right">&pound;{(item.price * item.quantity).toFixed(2)}</p>
                <button 
                  onClick={() => updateCartItem(item.product_id, 0)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`remove-item-${item.product_id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
          <button onClick={clearCart} className="text-red-500 hover:underline mt-4 text-sm">
            Clear Cart
          </button>
        </div>

        <div className="card p-6 h-fit" data-testid="cart-summary">
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          {(() => {
            const subtotal = cart.total;
            const delivery = subtotal >= 30 ? 0 : 4.99;
            const vat = +((subtotal + delivery) * 0.20).toFixed(2);
            const total = +((subtotal + delivery + vat)).toFixed(2);
            return (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between" data-testid="cart-subtotal">
                    <span>Subtotal (excl. VAT)</span>
                    <span>&pound;{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between" data-testid="cart-delivery">
                    <span>Delivery</span>
                    <span className={delivery === 0 ? "text-green-600" : ""}>{delivery === 0 ? "FREE" : `\u00A3${delivery.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between" data-testid="cart-vat">
                    <span>VAT (20%)</span>
                    <span>&pound;{vat.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span data-testid="cart-total">&pound;{total.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">inc VAT &amp; delivery</p>
                </div>
                <button onClick={() => navigate("/checkout")} className="btn-primary w-full mt-6" data-testid="checkout-btn">
                  Proceed to Checkout
                </button>
                {subtotal < 30 && (
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Add &pound;{(30 - subtotal).toFixed(2)} more for free delivery
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
